import { ExternalLink, Package, Truck } from "lucide-react";
import { Button } from "@/app/components/shadcn/button";
import { CargoTrackingResult } from "@/app/lib/features/shippingTracking/shippingTracking.types";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface ShippingTrackingResultProps {
  result: CargoTrackingResult;
}

const ShippingTrackingResult = ({ result }: ShippingTrackingResultProps) => {
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 lg:p-8 space-y-6">
      {/* Üst — sipariş no + durum */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-500">Sipariş Numarası</p>
          <p className="text-lg font-bold text-gray-900">
            {result.order_number}
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border w-fit"
          style={{
            backgroundColor: hexToRgba(result.status_color, 0.1),
            color: result.status_color,
            borderColor: hexToRgba(result.status_color, 0.3),
          }}
        >
          <Package className="w-4 h-4" />
          {result.status}
        </span>
      </div>

      <div className="h-px bg-gray-100" />

      {/* Kargo bilgileri */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Kargo Firması</p>
          <p className="font-semibold text-gray-900 flex items-center gap-1.5">
            <Truck className="w-4 h-4 text-gray-400" />
            {result.carrier}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Takip Numarası</p>
          <p className="font-semibold text-gray-900">
            {result.tracking_number}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Tahmini Teslimat</p>
          <p className="font-semibold text-gray-900">
            {result.estimated_delivery}
          </p>
        </div>
      </div>

      {result.tracking_url && (
        <Button variant="outline" className="w-full sm:w-auto" asChild>
          <a
            href={result.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackGeneric("shipping_carrier_track", "click")}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Kargo Firmasında Takip Et
          </a>
        </Button>
      )}

      <div className="h-px bg-gray-100" />

      {/* Tarihçe */}
      <div>
        <p className="text-sm font-semibold text-gray-900 mb-4">
          Kargo Geçmişi
        </p>
        <ol className="relative border-l border-gray-200 space-y-4 ml-2">
          {result.history.map((item, index) => (
            <li key={index} className="ml-4">
              <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white bg-main" />
              <p className="text-sm font-semibold text-gray-900">
                {item.status}
              </p>
              {item.description && (
                <p className="text-xs text-gray-500">{item.description}</p>
              )}
              <p className="text-xs text-gray-400 mt-0.5">{item.date}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default ShippingTrackingResult;
